import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import { StickyMobileBar } from "@/components/site/StickyMobileBar";
import { Reveal } from "@/components/site/Reveal";
import { company } from "@/data/content";

const Section = ({ eyebrow, title, children }) => (
  <Reveal className="border-t border-border py-10 first:border-t-0 first:pt-0">
    {eyebrow && (
      <p className="text-xs font-semibold uppercase tracking-[0.22em] text-accent">
        {eyebrow}
      </p>
    )}
    {title && (
      <h2 className="mt-3 text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
        {title}
      </h2>
    )}
    <div className="mt-5 max-w-3xl space-y-4 text-base leading-relaxed text-muted-foreground">
      {children}
    </div>
  </Reveal>
);

export default function PrivacyPolicy() {
  const today = new Date().toLocaleDateString("cs-CZ");
  return (
    <div className="min-h-screen bg-background" data-testid="privacy-page">
      <Navbar />
      <main className="pt-24 lg:pt-28">
        <section className="mx-auto max-w-7xl px-5 py-16 lg:px-10 lg:py-24">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-accent">
            Právní informace
          </p>
          <h1 className="mt-3 text-4xl font-semibold tracking-tight text-foreground sm:text-5xl">
            Ochrana osobních údajů
          </h1>
          <p className="mt-4 max-w-2xl text-base text-muted-foreground">
            Zpracování osobních údajů podle nařízení Evropského parlamentu
            a Rady (EU) 2016/679 (GDPR) a zákona č. 110/2019 Sb.
          </p>

          <div className="mt-14">
            <Section eyebrow="1" title="Správce osobních údajů">
              <p>
                Správcem osobních údajů je společnost{" "}
                <strong className="text-foreground">{company.legalName}</strong>,
                IČO {company.ico}, se sídlem v {company.city}, jednatel{" "}
                {company.owner}. Kontaktní údaje: e-mail{" "}
                <a
                  href={company.emailHref}
                  className="text-foreground underline underline-offset-4 transition-colors hover:text-accent"
                >
                  {company.email}
                </a>
                , telefon{" "}
                <a
                  href={company.phoneHref}
                  className="text-foreground underline underline-offset-4 transition-colors hover:text-accent"
                >
                  {company.phone}
                </a>
                .
              </p>
            </Section>

            <Section eyebrow="2" title="Rozsah zpracovávaných údajů">
              <p>
                Při vyplnění nezávazného poptávkového formuláře nebo přímém
                kontaktu (e-mail, telefon) zpracováváme následující údaje:
              </p>
              <ul className="list-disc space-y-2 pl-6">
                <li>jméno a příjmení,</li>
                <li>telefonní číslo,</li>
                <li>e-mailová adresa,</li>
                <li>lokalita, popis poptávky, předpokládaný termín,</li>
                <li>další údaje, které nám dobrovolně sdělíte.</li>
              </ul>
            </Section>

            <Section eyebrow="3" title="Účel a právní základ zpracování">
              <ul className="list-disc space-y-2 pl-6">
                <li>
                  <strong className="text-foreground">Vyřízení poptávky a jednání o smlouvě</strong> —
                  příprava cenové nabídky a související komunikace. Právní základ:
                  čl. 6 odst. 1 písm. b) GDPR (opatření přijatá před uzavřením smlouvy).
                </li>
                <li>
                  <strong className="text-foreground">Vedení evidence zakázek</strong> —
                  po případném uzavření smlouvy dále z důvodu plnění smlouvy
                  a našich zákonných povinností (např. účetní a daňové předpisy).
                </li>
                <li>
                  <strong className="text-foreground">Ochrana našich oprávněných zájmů</strong> —
                  zejména evidence poptávek a případná obhajoba právních nároků
                  (čl. 6 odst. 1 písm. f) GDPR).
                </li>
              </ul>
            </Section>

            <Section eyebrow="4" title="Doba uchování">
              <p>
                Údaje zpracováváme po dobu nezbytnou k naplnění účelu — obvykle
                po dobu jednání o zakázce a následně po dobu vyžadovanou
                právními předpisy (zejména daňovými a účetními, tj. zpravidla
                až 10 let). Nevyužité poptávky mažeme nejpozději do 3 let od
                posledního kontaktu.
              </p>
            </Section>

            <Section eyebrow="5" title="Příjemci osobních údajů">
              <p>
                Osobní údaje můžeme předávat těmto kategoriím příjemců, a to
                pouze v nezbytném rozsahu:
              </p>
              <ul className="list-disc space-y-2 pl-6">
                <li>
                  poskytovatelé IT služeb, kteří zajišťují chod webu, e-mailové
                  komunikace a evidence poptávek (např. Google — Google Sheets,
                  Resend — doručování e-mailů),
                </li>
                <li>
                  účetní, daňoví a právní poradci,
                </li>
                <li>
                  orgány veřejné moci v případech stanovených zákonem.
                </li>
              </ul>
              <p>
                Osobní údaje nepředáváme mimo Evropský hospodářský prostor,
                pokud tak nevyžadují smluvní podmínky ověřených poskytovatelů;
                v takovém případě je předání zajištěno standardními smluvními
                doložkami podle čl. 46 GDPR.
              </p>
            </Section>

            <Section eyebrow="6" title="Vaše práva">
              <p>V souvislosti se zpracováním osobních údajů máte právo:</p>
              <ul className="list-disc space-y-2 pl-6">
                <li>na přístup k údajům a jejich kopii,</li>
                <li>na opravu nepřesných nebo neúplných údajů,</li>
                <li>na výmaz („právo být zapomenut"),</li>
                <li>na omezení zpracování,</li>
                <li>na přenositelnost údajů,</li>
                <li>
                  vznést námitku proti zpracování založenému na oprávněném
                  zájmu,
                </li>
                <li>
                  podat stížnost u dozorového úřadu — Úřad pro ochranu osobních
                  údajů, Pplk. Sochora 27, 170 00 Praha 7, www.uoou.cz.
                </li>
              </ul>
              <p>
                Svá práva můžete uplatnit e-mailem na{" "}
                <a
                  href={company.emailHref}
                  className="text-foreground underline underline-offset-4 transition-colors hover:text-accent"
                >
                  {company.email}
                </a>
                .
              </p>
            </Section>

            <Section eyebrow="7" title="Zabezpečení">
              <p>
                Osobní údaje uchováváme v zabezpečeném prostředí. Přístup k nim
                mají pouze osoby, které údaje potřebují ke své činnosti, a jsou
                vázány mlčenlivostí.
              </p>
            </Section>

            <Section eyebrow="8" title="Závěrečná ustanovení">
              <p>
                Tato pravidla mohou být průběžně aktualizována — aktuální znění
                naleznete vždy na této stránce.
              </p>
              <p className="text-sm">
                Aktuální znění platné od: {today}.
              </p>
            </Section>
          </div>

          <Link
            to="/"
            data-testid="privacy-back-home"
            className="mt-14 inline-flex items-center gap-2 text-sm font-semibold text-accent transition-colors hover:text-accent/80"
          >
            <ArrowLeft className="h-4 w-4" /> Zpět na hlavní stránku
          </Link>
        </section>
      </main>
      <Footer />
      <StickyMobileBar />
    </div>
  );
}
